import { isIPv6, isIPv6CIDR } from './ipAddressValidations';

export const removeIPv6Duplicates = (addresses: string[]): string[] => {
  const seen = new Set<string>();
  const uniqueAddresses: string[] = [];

  for (const address of addresses) {
    if (!isIPv6(address) && !isIPv6CIDR(address)) {
      uniqueAddresses.push(address);
      continue;
    }

    const normalized = normalizeIPv6WithCIDR(address);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      uniqueAddresses.push(address);
    }
  }

  return uniqueAddresses;
};

const normalizeIPv6WithCIDR = (input: string): string => {
  const [address, cidr] = input.split('/');
  const expandedAddress = expandIPv6(address!.toLowerCase());

  return cidr
    ? `${expandedAddress}/${Number.parseInt(cidr, 10)}`
    : expandedAddress;
};

const expandIPv6 = (address: string): string => {
  let normalizedNotation = address;

  // Detect IPv4-mapped IPv6 (e.g., ::192.0.2.128)
  const ipv4Match = normalizedNotation.match(
    /^(.*):(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/,
  );
  if (ipv4Match) {
    const [, ipv6Part, ipv4Part] = ipv4Match;
    // Convert IPv4 to hex blocks
    // Both parts must be present when the regex was matched.
    const ipv4Blocks = ipv4Part!.split('.').map(Number);
    if (
      ipv4Blocks.length === 4 &&
      ipv4Blocks.every((b) => b >= 0 && b <= 255)
    ) {
      // ! on ipv4Blocks: all blocks are verified to be present in the condition above.
      const hex1 = ((ipv4Blocks[0]! << 8) | ipv4Blocks[1]!)
        .toString(16)
        .padStart(4, '0');
      const hex2 = ((ipv4Blocks[2]! << 8) | ipv4Blocks[3]!)
        .toString(16)
        .padStart(4, '0');
      normalizedNotation = `${ipv6Part}:${hex1}:${hex2}`;
    }
  }

  const hasDoubleColon = normalizedNotation.includes('::');
  const blocks: string[] = [];

  if (hasDoubleColon) {
    const [head = '', tail = ''] = normalizedNotation.split('::');
    const headBlocks = head ? head.split(':') : [];
    const tailBlocks = tail ? tail.split(':') : [];

    const missingBlockCount = 8 - (headBlocks.length + tailBlocks.length);
    if (missingBlockCount < 0) {
      throw new Error('Too many IPv6 segments');
    }

    blocks.push(
      ...headBlocks,
      ...Array(missingBlockCount).fill('0'),
      ...tailBlocks,
    );
  } else {
    blocks.push(...normalizedNotation.split(':'));
  }

  if (blocks.length !== 8) {
    throw new Error('Invalid IPv6 block count');
  }

  return blocks.map((block) => block.padStart(4, '0')).join(':');
};
